import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    message,
    Typography,
    Form,
    Input,
    Card,
    Spin,
    Row,
    Col,
    Space,
    Button,
    Select,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserApi } from '../../../../api/user/userApp';
import { TContact, TPassword, TUserApp, TUserAppRelation } from '../../../../models/user/User';
import { determineCreationContext } from '../../../../utils/creationContext';
import AppLayout from '../../../../components/layout/AppLayout';
import PersonalInfoForm from './PersonalInfoForm';
import BusinessInfoForm from './BusinessInfoForm';
import ContactsManager from './ContactsManager';
import { useReferential } from '../../../../context/ReferentialContext';
import { CODIFICATION_TYPE, CODIFICATION_TYPE_RELATION, CODIFICATION_TYPE_USERS } from '../../../../constant/codification';
import { TCodification } from '../../../../models/referential/referential';
import RelationManager from './RelationManager';
import './UserForm.css';
import { useTilmiAuth } from '../../../../hooks/useTilmiAuth';

/**
 * Normalise un identifiant de connexion saisi à la main : minuscules, sans
 * accents ni espaces, et limité aux caractères saisissables au clavier.
 * Aligné sur ce que produit la génération automatique côté serveur.
 */
const normalizeLogin = (value: string | undefined): string =>
    (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // retire les diacritiques
        .toLowerCase()
        .replace(/[^a-z0-9._-]/g, '');

// Inline country select using referential context
const CountrySelect: React.FC<any> = (props) => {
    const { countries } = useReferential();
    return (
        <Select
            showSearch
            allowClear
            placeholder="Sélectionnez un pays"
            optionFilterProp="label"
            options={countries.map((c) => ({ value: c.code, label: c.name }))}
            {...props}
        />
    );
};

const UserForm: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { getCodificationByCodes, getCodificationById } = useReferential();

    const { getUserDetails, saveUserWithDetails, generateLoginUserApp, updateUserMetadata } = useUserApi();
    const { getUserAppId, getCodeTypeUser } = useTilmiAuth();
    const [userApp, setUserApp] = useState<TUserApp | null>(null);
    const [loading, setIsLoading] = useState<boolean>(false);
    const [generatingLogin, setGeneratingLogin] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [form] = Form.useForm();

    const userAppId: number | null = location.state?.userAppId || null;
    const isEditMode = !!userAppId;
    const typeUser: TCodification = location.state?.typeUser;

    // relations types
    const codif_teacher_student = getCodificationByCodes(
        CODIFICATION_TYPE.TYPE_RELATION,
        CODIFICATION_TYPE_RELATION.TEACHER_STUDENT
    );
    const codif_parent_student = getCodificationByCodes(
        CODIFICATION_TYPE.TYPE_RELATION,
        CODIFICATION_TYPE_RELATION.PARENT_STUDENT
    );

    const [typeUserLoaded, setTypeUserLoaded] = useState<TCodification | null>(typeUser ?? null);
    useEffect(() => {
        setTypeUserLoaded(typeUser);
    }, [typeUser]);

    useEffect(() => {
        if (isEditMode) {
            setIsLoading(true);
            getUserDetails(userAppId!)
                .then((resp) => {
                    const result: TUserApp = resp.data;

                    const typeUserApp = getCodificationById(result.usertype_id);
                    if (typeUserApp) setTypeUserLoaded(typeUserApp);

                    setUserApp(result);
                    form.setFieldsValue({
                        ...resp.data,
                        personal_info: {
                            ...resp.data.personal_info,
                            birthdate: resp.data.personal_info?.birthdate
                                ? resp.data.personal_info.birthdate.substring(0, 10)
                                : undefined,
                        },
                    });
                })
                .catch((err) => {
                    message.error(getErrorMessage(err));
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
        // eslint-disable-next-line
    }, [userAppId]);

    const generateLogin = async () => {
        try {
            const values = await form.validateFields(['personal_info', 'business_info']);
            const firstname = values.personal_info?.firstname;
            const lastname = values.personal_info?.lastname;
            const businessname = values.business_info?.name;

            if (!businessname && (!firstname || !lastname)) {
                message.error(t('message.names_required', { min: 2 }));
                return;
            }
            if (firstname && lastname && (firstname.length < 2 || lastname.length < 2)) {
                message.error(t('message.min_len', { len: 2 }));
                return;
            } else if (businessname && businessname.length < 2) {
                message.error(t('message.min_len', { len: 2 }));
                return;
            }
            setGeneratingLogin(true);

            const payload = {
                firstname: firstname || 'school',
                lastname: lastname || businessname,
            };
            const response: any = await generateLoginUserApp(payload);
            const generatedLogin = response.data.login;
            form.setFieldsValue({ login: generatedLogin });
        } catch (err: any) {
            console.error('Error generating login:', err);
            message.error(getErrorMessage(err));
        } finally {
            setGeneratingLogin(false);
        }
    };

    const onFinish = async (values: any): Promise<void> => {
        if (saving) return; // anti double-click
        setSaving(true);

        try {
            // Auto-generate login if missing
            if (!values.login) {
                const firstname = values.personal_info?.firstname || 'school';
                const lastname = values.personal_info?.lastname || values.business_info?.name || 'user';
                try {
                    const resp = await generateLoginUserApp({ firstname, lastname });
                    const generated = resp?.data?.login;
                    if (generated) {
                        values.login = generated;
                        form.setFieldsValue({ login: generated });
                    }
                } catch (e) {
                    // continue without auto-generation; backend will validate
                }
            }

            // Contacts valides uniquement
            const validContacts = (values.contacts || []).filter((c: TContact) => c.type_id && c.contact_value);

            // Récup relations depuis le form
            const allValues = [
                ...(values.teachers || []).filter((item: any) => item != null),
                ...(values.parents || []).filter((item: any) => item != null),
                ...(values.students || []).filter((item: any) => item != null),
            ];

            const ALLOWED_USER_TYPES = [
                CODIFICATION_TYPE_USERS.TEACHER,
                CODIFICATION_TYPE_USERS.ADULT,
                CODIFICATION_TYPE_USERS.MINOR,
            ];

            const isAllowedType = !!(typeUserLoaded && ALLOWED_USER_TYPES.includes(typeUserLoaded.code));

            const relations: TUserAppRelation[] = isAllowedType
                ? (allValues || []).map((item: any) => {
                    if (typeUserLoaded?.code === CODIFICATION_TYPE_USERS.MINOR) {
                        return {
                            typerelation_id: item.typeRelationCodification.id,
                            parent_id: item.id,
                            children_id: userApp?.id || 0,
                        } as TUserAppRelation;
                    }
                    return {
                        typerelation_id: item.typeRelationCodification.id,
                        parent_id: userApp?.id || 0,
                        children_id: item.id,
                    } as TUserAppRelation;
                })
                : [];

            // Override: if a parent is creating a student, force parent-student relation and hide choices in UI
            const creatorTypeCode = getCodeTypeUser();
            const isMinorType = typeUserLoaded?.code === CODIFICATION_TYPE_USERS.MINOR;
            const isParentCreator = creatorTypeCode === CODIFICATION_TYPE_USERS.ADULT;

            let finalRelations: TUserAppRelation[] = relations;
            if (isMinorType && isParentCreator) {
                const parentId = Number(getUserAppId() || 0);
                const alreadyLinked = (userApp?.userapp_relations || []).some(
                    (r) => r.typerelation_id === (codif_parent_student?.id || -1) && r.parent_id === parentId
                );
                if (!alreadyLinked && codif_parent_student?.id && parentId > 0) {
                    finalRelations = [{
                        typerelation_id: codif_parent_student.id,
                        parent_id: parentId,
                        children_id: userApp?.id || 0,
                    } as TUserAppRelation];
                }
            }

            // Format date strictly RFC3339, and drop null to avoid backend bind error
            if (values?.personal_info?.birthdate) {
                const bd = values.personal_info.birthdate;
                if (typeof bd === 'string' && bd.length === 10) {
                    // YYYY-MM-DD from DatePickerHybrid → full ISO expected by API
                    values.personal_info.birthdate = new Date(bd + 'T00:00:00.000Z').toISOString();
                } else if (typeof bd?.toISOString === 'function') {
                    // legacy dayjs object fallback
                    values.personal_info.birthdate = bd.toISOString();
                }
            } else if (values?.personal_info) {
                // Remove birthdate if null/undefined to not send JSON null for time.Time
                delete values.personal_info.birthdate;
            }

            if (!typeUserLoaded) {
                message.error(t('message.bugBackend'));
                throw new Error(t('message.bugBackend'));
            }

            // Password uniquement si saisi
            const wantPassword = !!values.password;
            const passwordArr: TPassword[] | undefined = wantPassword
                ? [{ id: 0, userapp_id: 0, password: values.password }]
                : undefined;

            // Calcul du creation_context (uniquement à la création, pas en édition)
            const isCreation = !(userApp?.id && userApp.id > 0);
            const actorUsertype = creatorTypeCode ?? '';
            const targetUsertype = typeUserLoaded.code ?? '';
            // Un administrateur n'est soumis à aucun creation_context : ceux-ci
            // décrivent une DÉLÉGATION (un parent crée son enfant, une école crée
            // son animateur), pas l'administration de la plateforme. Ses créations
            // passent donc par le chemin legacy, qu'il s'agisse d'un opérateur de
            // cours, d'une organisation ou d'un compte de service.
            //
            // Auparavant seul « admin crée un opérateur de cours » était excepté :
            // créer une organisation échouait sur « Combinaison acteur/type de
            // compte non autorisée », alors que le serveur l'accepte. Chaque
            // nouveau type aurait exigé une dérogation de plus.
            const isAdminActor = actorUsertype === CODIFICATION_TYPE_USERS.ADMIN;
            const creationCtx = isCreation && !isAdminActor
                ? determineCreationContext(actorUsertype, targetUsertype)
                : undefined;

            if (isCreation && !isAdminActor && creationCtx === null) {
                message.error('Combinaison acteur/type de compte non autorisée.');
                setSaving(false);
                return;
            }

            // Payload propre : on omet les champs vides
            const payload: TUserApp = {
                id: userApp?.id || 0,
                login: values.login,
                email: (values.email || '').trim(),
                usertype_id: typeUserLoaded.id,
                max_session: 1,
                personal_info: values.personal_info,
                business_info: values.business_info,
                contacts: validContacts,
                ...(passwordArr ? { passwords: passwordArr } : {}),
                ...(finalRelations.length > 0 ? { userapp_relations: finalRelations } : {}),
                ...(creationCtx ? { creation_context: creationCtx } : {}),
            };

            const resp = await saveUserWithDetails(payload);
            const saved: TUserApp = resp.data.user;
            if (resp.data.email_sent === false && typeUserLoaded.code !== 'MINOR') {
                message.warning(t('message.emailNotSent', "Compte créé mais l'email d'activation n'a pas pu être envoyé. Contactez le support."));
            }

            // If a country has been selected, persist it in user metadata (pays)
            const selectedCountry: string | undefined = values?.country;
            if (saved?.id && selectedCountry) {
                try {
                    await updateUserMetadata(saved.id, { pays: selectedCountry });
                } catch (metaErr) {
                    // Non-bloquant: on informe seulement
                    console.warn('Failed to update user metadata (pays):', metaErr);
                }
            }

            message.success(t('message.savedSuccessfully'));

            if (!userApp?.id && saved?.id) {
                navigate('/conf/users');
                return;
            }

            form.setFieldValue('password', undefined);
        } catch (err: any) {
            message.error(getErrorMessage(err));
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        if (saving) return;
        try {
            const values = await form.validateFields();
            await onFinish(values);
        } catch (error) {
            console.error('An error occurred while saving:', error);
        }
    };

    const handleBackNavigation = () => {
        navigate(-1);
    };

    return (
        <AppLayout
            title={(userApp && userApp.id > 0 ? t('text.update') : t('text.add')) + ' : ' + (typeUserLoaded && typeUserLoaded.label)}
            loading={loading}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
            <div className="user-form-container">
                <Spin spinning={loading}>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        onFinishFailed={(errorInfo) => {
                            console.error('Failed:', errorInfo);
                        }}
                        initialValues={
                            userApp
                                ? undefined
                                : {
                                    max_session: 1,
                                    personal_info: {
                                        firstname: '',
                                        lastname: '',
                                    },
                                }
                        }
                    >
                        {/* Première ligne */}
                        <Row gutter={[16, 16]}>
                            {typeUserLoaded && typeUserLoaded.code !== CODIFICATION_TYPE_USERS.BUSINESS && (
                                <Col xs={24} sm={24} md={8}>
                                    <PersonalInfoForm />
                                    {/* Country selector: required when creating ADULT (Parent) */}
                                    {!isEditMode && typeUserLoaded.code === CODIFICATION_TYPE_USERS.ADULT && (
                                        <Card title={t('text.country', 'Pays')} style={{ marginTop: 12 }}>
                                            <Form.Item
                                                name="country"
                                                label={t('text.country', 'Pays')}
                                                rules={[{ required: true, message: t('message.value_required') }]}
                                            >
                                                <CountrySelect />
                                            </Form.Item>
                                        </Card>
                                    )}
                                    {/* Country selector: shown in edit mode for child (MINOR) accounts */}
                                    {isEditMode && typeUserLoaded.code === CODIFICATION_TYPE_USERS.MINOR && (
                                        <Card title={t('text.country', 'Pays')} style={{ marginTop: 12 }}>
                                            <Form.Item name="country" label={t('text.country', 'Pays')}>
                                                <CountrySelect />
                                            </Form.Item>
                                        </Card>
                                    )}
                                </Col>
                            )}

                            {typeUserLoaded && typeUserLoaded.code === CODIFICATION_TYPE_USERS.BUSINESS && (
                                <Col xs={24} sm={24} md={8}>
                                    <BusinessInfoForm />
                                    {/* Country selector: required when creating BUSINESS (Organization) */}
                                    {!isEditMode && (
                                        <Card title={t('text.country', 'Pays')} style={{ marginTop: 12 }}>
                                            <Form.Item
                                                name="country"
                                                label={t('text.country', 'Pays')}
                                                rules={[{ required: true, message: t('message.value_required') }]}
                                            >
                                                <CountrySelect />
                                            </Form.Item>
                                        </Card>
                                    )}
                                </Col>
                            )}

                            <Col xs={24} sm={24} md={8}>
                                <Card title={t('text.user_info')} style={{ marginBottom: 0 }}>
                                    <Form.Item
                                        label={t('text.login', 'Identifiant de connexion')}
                                        required
                                        style={{ marginBottom: 4 }}
                                    >
                                        <Space.Compact style={{ width: '100%' }}>
                                            <Form.Item
                                                name="login"
                                                noStyle
                                                // Le login sert à se connecter : on le normalise à la saisie
                                                // pour éviter majuscules, espaces et accents.
                                                normalize={normalizeLogin}
                                                rules={[
                                                    { required: true, message: t('message.value_required') },
                                                    { min: 2, message: t('message.min_len', { len: 2 }) },
                                                ]}
                                            >
                                                <Input placeholder={t('text.loginPlaceholder', 'ex. yassine2')} />
                                            </Form.Item>
                                            <Button type="primary" onClick={generateLogin} loading={generatingLogin}>
                                                Générer
                                            </Button>
                                        </Space.Compact>
                                    </Form.Item>
                                    <Typography.Text
                                        type="secondary"
                                        style={{ fontSize: 12, display: 'block', marginBottom: 16 }}
                                    >
                                        {t(
                                            'text.loginHint',
                                            'Modifiable. « Générer » propose un identifiant à partir du prénom et du nom.'
                                        )}
                                    </Typography.Text>

                                    <Form.Item label="Email" name="email" rules={[{ type: 'email', message: 'Email invalide' }]}>
                                        <Input placeholder="name@example.com" />
                                    </Form.Item>

                                    <Form.Item label={t('text.password')} name="password">
                                        <Input.Password />
                                    </Form.Item>
                                </Card>
                            </Col>

                            <Col xs={24} sm={24} md={8}>
                                <ContactsManager />
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            {typeUserLoaded && typeUserLoaded.code === CODIFICATION_TYPE_USERS.MINOR && (
                                <>
                                    {getCodeTypeUser() !== CODIFICATION_TYPE_USERS.ADULT && (
                                        <>
                                            <Col
                                                xs={24}
                                                sm={24}
                                                md={12}
                                                style={{ display: 'flex', flexDirection: 'column', justifyContent: 'stretch' }}
                                            >
                                                <RelationManager
                                                    title="Parent"
                                                    typeUser={typeUserLoaded}
                                                    codifTypeRelation={codif_parent_student}
                                                    userApp={userApp}
                                                    fieldName="parents"
                                                />
                                            </Col>
                                            <Col
                                                xs={24}
                                                sm={24}
                                                md={12}
                                                style={{ display: 'flex', flexDirection: 'column', justifyContent: 'stretch' }}
                                            >
                                                <RelationManager
                                                    title="Teacher"
                                                    typeUser={typeUserLoaded}
                                                    codifTypeRelation={codif_teacher_student}
                                                    userApp={userApp}
                                                    fieldName="teachers"
                                                />
                                            </Col>
                                        </>
                                    )}
                                </>
                            )}

                            {typeUserLoaded && typeUserLoaded.code === CODIFICATION_TYPE_USERS.ADULT && (
                                <>
                                    <Col
                                        xs={24}
                                        sm={24}
                                        md={12}
                                        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'stretch' }}
                                    >
                                        <RelationManager
                                            title="Enfant"
                                            typeUser={typeUserLoaded}
                                            codifTypeRelation={codif_parent_student}
                                            userApp={userApp}
                                            fieldName="students"
                                        />
                                    </Col>
                                </>
                            )}

                            {typeUserLoaded && typeUserLoaded.code === CODIFICATION_TYPE_USERS.TEACHER && (
                                <>
                                    <Col
                                        xs={24}
                                        sm={24}
                                        md={12}
                                        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'stretch' }}
                                    >
                                        <RelationManager
                                            title="Élève"
                                            typeUser={typeUserLoaded}
                                            codifTypeRelation={codif_teacher_student}
                                            userApp={userApp}
                                            fieldName="students"
                                        />
                                    </Col>
                                </>
                            )}
                        </Row>
                    </Form>
                </Spin>

                <button
                    className="user-form-save-button"
                    onClick={handleSave}
                    disabled={saving || loading}
                    type="button"
                >
                    <SaveOutlined style={{ fontSize: 24 }} />
                </button>
            </div>
        </AppLayout>
    );
};

export default UserForm;
const getErrorMessage = (err: any): string => {
    try {
        const data = err?.response?.data ?? err?.data ?? err;
        if (typeof data === 'string') return data;
        if (data && typeof data === 'object') {
            // Common API error envelope: { code, message, status }
            if (typeof data.message === 'string' && data.message.trim() !== '') return data.message;
            // Axios might wrap again under data
            if (typeof data?.error === 'string') return data.error;
            return JSON.stringify(data);
        }
        return err?.message || String(err) || 'Erreur inconnue';
    } catch {
        return 'Erreur inconnue';
    }
};
